import { useEffect, useRef, useState } from "react";
import { CATEGORICAL, colorFor } from '../lib/definitions/palette';
import type { ThemeMode } from '../lib/definitions/palette';

// Telemetry section rebuild (2026-09-24): "telemetry needs to be nice
// looking graphs, not a string of cards. ONE graph for ONE type of
// telemetry a dot graph with lines to see trends" — verbatim requirement.
// No charting library added (bundle is already 1.45MB and Vite already
// warns about it) — this is a small, self-contained SVG dots-plus-line
// plot. One <TelemetryChart> per metric; NodeDetailPage.tsx owns pivoting
// the raw/hourly telemetry-history rows into per-metric point series and
// building the value/time formatters this component is handed — this file
// only knows how to draw {t, v} pairs, never a metric name, unit, or
// field-specific formatting rule (those live in NodeDetailPage.tsx's
// existing TELEMETRY_FIELD_UNIT / titleizeTelemetryField /
// formatTelemetryValue, reused rather than duplicated).

export interface TelemetryChartPoint {
  t: number; // epoch ms
  v: number;
}

const CHART_HEIGHT = 132;
const MARGIN = { top: 10, right: 10, bottom: 18, left: 44 };
const TOOLTIP_W = 104;
const TOOLTIP_H = 34;

export function TelemetryChart({
  title,
  latestLabel,
  points,
  theme,
  formatValue,
  formatTime,
  yDomain,
}: {
  // Human label + unit, e.g. "Temperature (°C)" — already assembled by the
  // caller from titleizeTelemetryField/TELEMETRY_FIELD_UNIT.
  title: string;
  // Pre-formatted latest value, e.g. "3.83 V" or "Powered" — from the
  // caller's own formatTelemetryValue/batteryLabel convention.
  latestLabel: string;
  // Already merged (multi-observer) and sorted ascending by t.
  points: TelemetryChartPoint[];
  theme: ThemeMode;
  formatValue: (v: number) => string;
  formatTime: (t: number) => string;
  // Fixed [min, max] y axis instead of auto-scaling to the data range —
  // NodeDetailPage.tsx passes this for any metric whose unit is "%", so a
  // battery that only ever read 0-25% still plots against a full 0-100
  // scale rather than looking topped off. Points outside the domain (e.g.
  // battery_level's >100 "powered" convention) are clamped to the nearest
  // edge for plotting only — formatValue still receives the true raw value
  // for axis/tooltip text, so a powered reading pins visually at the 100%
  // line rather than being drawn as if it were some percentage above it.
  yDomain?: [number, number];
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(280);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (points.length === 0) {
    return (
      <div className="telemetry-chart-card">
        <div className="telemetry-chart-header">
          <span className="telemetry-chart-title">{title}</span>
        </div>
        <div className="telemetry-chart-empty">No samples.</div>
      </div>
    );
  }

  const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 10);
  const plotHeight = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

  // Avoid Math.min/max(...spread) — a 30-day raw retention window can carry
  // several hundred points for a chatty metric, and spread-into-apply has a
  // real (engine-dependent) argument-count ceiling.
  let rawMin = points[0].v;
  let rawMax = points[0].v;
  for (const p of points) {
    if (p.v < rawMin) rawMin = p.v;
    if (p.v > rawMax) rawMax = p.v;
  }
  let yMin: number;
  let yMax: number;
  // Axis label values — normally the real data min/max, but a fixed
  // yDomain shows its own [0, 100] edges instead, since the axis no
  // longer tracks where the data actually fell.
  let axisMaxValue: number;
  let axisMinValue: number;
  if (yDomain) {
    [yMin, yMax] = yDomain;
    axisMinValue = yMin;
    axisMaxValue = yMax;
  } else {
    yMin = rawMin;
    yMax = rawMax;
    // All-identical-value case: a zero-height range would collapse the plot
    // to a flat line sitting on the axis. Pad it so there's still a visible
    // band, while the axis labels below keep reporting the real (equal)
    // min/max, not the padded ones.
    if (yMin === yMax) {
      const pad = Math.max(Math.abs(yMin) * 0.1, 1);
      yMin -= pad;
      yMax += pad;
    }
    axisMinValue = rawMin;
    axisMaxValue = rawMax;
  }

  const tMin = points[0].t;
  const tMax = points[points.length - 1].t;

  function xFor(t: number): number {
    if (tMax === tMin) return MARGIN.left + plotWidth / 2;
    return MARGIN.left + ((t - tMin) / (tMax - tMin)) * plotWidth;
  }
  function yFor(v: number): number {
    // Clamp to the axis range so a value outside a fixed yDomain (e.g.
    // battery_level's >100 "powered" reading against the 0-100 axis)
    // pins at the nearest edge instead of drawing off-chart or implying a
    // scale beyond what the axis shows. A no-op for the auto-scaled case,
    // since yMin/yMax already bound the data there.
    const clamped = Math.min(yMax, Math.max(yMin, v));
    return MARGIN.top + plotHeight - ((clamped - yMin) / (yMax - yMin)) * plotHeight;
  }

  const lineColor = colorFor(CATEGORICAL.blue, theme);

  // A single point draws a dot and no line — the path is only built (and
  // only rendered) once there's a second point to connect it to. Always
  // built from the FULL points array (never the downsampled display set
  // below) — the line must never misrepresent the data's real shape.
  const pathD =
    points.length > 1 ? points.map((p, i) => `${i === 0 ? "M" : "L"}${xFor(p.t).toFixed(1)},${yFor(p.v).toFixed(1)}`).join(" ") : "";

  // Hit-testing (2026-09-24 fix): a dense series used to get one invisible
  // r=8 hit-target <circle> PER POINT, each with its own onMouseEnter/
  // onMouseLeave — on a node detail page with ~1,229 telemetry points
  // across its charts that was 2,458 extra SVG elements and listeners,
  // measured as 56% of the whole page's DOM and the direct cause of
  // laggy scrolling (main-thread work re-evaluating all those listeners/
  // hit-boxes on every scroll-driven layout pass). Replaced with ONE
  // transparent overlay <rect> per chart and a single onMouseMove handler
  // that maps the pointer's x position straight to the nearest data
  // point by binary-searching the (already time-sorted) full points
  // array — no per-point listener at all.
  function nearestIndexForX(clientX: number, svgLeft: number): number {
    if (points.length === 1) return 0;
    const localX = Math.min(MARGIN.left + plotWidth, Math.max(MARGIN.left, clientX - svgLeft));
    if (tMax === tMin) return points.length - 1;
    const t = tMin + ((localX - MARGIN.left) / plotWidth) * (tMax - tMin);
    let lo = 0;
    let hi = points.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (points[mid].t < t) lo = mid + 1;
      else hi = mid;
    }
    if (lo > 0 && Math.abs(points[lo - 1].t - t) <= Math.abs(points[lo].t - t)) return lo - 1;
    return lo;
  }

  function handlePointerMove(e: React.MouseEvent<SVGRectElement>) {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    setHoverIdx(nearestIndexForX(e.clientX, svg.getBoundingClientRect().left));
  }

  // Downsample which dots are actually DRAWN — several charts carry 200+
  // points in a plot only a few hundred pixels wide, where most dots
  // already overlap into an indistinguishable smear. Caps visible dots to
  // roughly one per 3px of plot width (dots are ~4.5-7px across, so that
  // still avoids most overlap) while the line above is always drawn from
  // the FULL series and hit-testing above always searches the FULL
  // series — downsampling only ever changes which dots are painted, never
  // the shape of the line or which point a hover finds.
  const maxVisibleDots = Math.max(20, Math.round(plotWidth / 3));
  const visibleIndices = new Set<number>();
  if (points.length <= maxVisibleDots) {
    for (let i = 0; i < points.length; i++) visibleIndices.add(i);
  } else {
    const step = (points.length - 1) / (maxVisibleDots - 1);
    for (let i = 0; i < maxVisibleDots; i++) visibleIndices.add(Math.round(i * step));
  }
  // The hovered point always gets its own dot drawn, even when it was
  // downsampled away above — the enlarged hover dot must always appear
  // under the cursor.
  if (hoverIdx !== null) visibleIndices.add(hoverIdx);
  const dotIndices = Array.from(visibleIndices).sort((a, b) => a - b);

  const hovered = hoverIdx !== null ? points[hoverIdx] : null;
  let tooltipX = 0;
  let tooltipY = 0;
  if (hovered) {
    tooltipX = xFor(hovered.t) + 8;
    if (tooltipX + TOOLTIP_W > MARGIN.left + plotWidth) tooltipX = xFor(hovered.t) - TOOLTIP_W - 8;
    if (tooltipX < 0) tooltipX = MARGIN.left;
    tooltipY = yFor(hovered.v) - TOOLTIP_H - 6;
    if (tooltipY < 0) tooltipY = yFor(hovered.v) + 10;
  }

  return (
    <div className="telemetry-chart-card">
      <div className="telemetry-chart-header">
        <span className="telemetry-chart-title">{title}</span>
        <span className="telemetry-chart-latest">{latestLabel}</span>
      </div>
      <div className="telemetry-chart-svg-wrap" ref={wrapRef}>
        <svg
          className="telemetry-chart-svg"
          width={width}
          height={CHART_HEIGHT}
          role="img"
          aria-label={`${title} over time, latest ${latestLabel}`}
        >
          <line className="telemetry-chart-gridline" x1={MARGIN.left} y1={yFor(yMax)} x2={MARGIN.left + plotWidth} y2={yFor(yMax)} />
          <line className="telemetry-chart-gridline" x1={MARGIN.left} y1={yFor(yMin)} x2={MARGIN.left + plotWidth} y2={yFor(yMin)} />
          <text className="telemetry-chart-axis-label" x={MARGIN.left - 5} y={yFor(yMax) + 3} textAnchor="end">
            {formatValue(axisMaxValue)}
          </text>
          <text className="telemetry-chart-axis-label" x={MARGIN.left - 5} y={yFor(yMin) + 3} textAnchor="end">
            {formatValue(axisMinValue)}
          </text>
          <text className="telemetry-chart-axis-label" x={MARGIN.left} y={CHART_HEIGHT - 4} textAnchor="start">
            {formatTime(tMin)}
          </text>
          <text className="telemetry-chart-axis-label" x={MARGIN.left + plotWidth} y={CHART_HEIGHT - 4} textAnchor="end">
            {formatTime(tMax)}
          </text>

          {pathD && <path className="telemetry-chart-line" d={pathD} stroke={lineColor} />}

          {dotIndices.map((i) => {
            const p = points[i];
            return (
              <circle
                key={i}
                className="telemetry-chart-dot"
                cx={xFor(p.t)}
                cy={yFor(p.v)}
                r={hoverIdx === i ? 3.5 : 2.25}
                fill={lineColor}
                pointerEvents="none"
              />
            );
          })}

          {hovered && (
            <g className="telemetry-chart-tooltip" pointerEvents="none">
              <line
                x1={xFor(hovered.t)}
                y1={MARGIN.top}
                x2={xFor(hovered.t)}
                y2={MARGIN.top + plotHeight}
                stroke={lineColor}
                strokeOpacity={0.35}
              />
              <rect className="telemetry-chart-tooltip-bg" x={tooltipX} y={tooltipY} width={TOOLTIP_W} height={TOOLTIP_H} rx={4} />
              <text className="telemetry-chart-tooltip-text" x={tooltipX + 6} y={tooltipY + 14}>
                {formatTime(hovered.t)}
              </text>
              <text className="telemetry-chart-tooltip-text telemetry-chart-tooltip-value" x={tooltipX + 6} y={tooltipY + 27}>
                {formatValue(hovered.v)}
              </text>
            </g>
          )}

          {/* Single hit-testing surface for the whole plot area — see the
              hit-testing comment above nearestIndexForX. Drawn last (on
              top) so it always receives the pointer events; the tooltip
              group above it is pointerEvents="none" so it never blocks
              this. */}
          <rect
            x={MARGIN.left}
            y={MARGIN.top}
            width={plotWidth}
            height={plotHeight}
            fill="transparent"
            onMouseMove={handlePointerMove}
            onMouseLeave={() => setHoverIdx(null)}
          />
        </svg>
      </div>
    </div>
  );
}
